/**
 * TELA 8 — USUÁRIOS
 *
 * Duas checagens diferentes, sempre as duas: a **permissão** responde "pode
 * fazer?"; o **escopo** responde "sobre quais registros?". Um supervisor com
 * permissão de editar só edita dentro da própria equipe.
 *
 * Por isso a hierarquia não é enfeite: ela É o escopo. Uma cadeia quebrada —
 * ciclo, superior inativo, usuário órfão — não deixa a tela feia, deixa
 * registros invisíveis para quem deveria enxergá-los.
 *
 * As quatro regras que o formulário faz cumprir, herdadas do protótipo:
 *  1. administrador não responde a ninguém;
 *  2. os demais perfis exigem superior;
 *  3. nunca pode faltar administrador ativo;
 *  4. nenhuma alteração pode fechar ciclo na cadeia.
 *
 * A tela valida antes de enviar para não gastar ida ao servidor com o que já
 * se sabe inválido. **Quem aplica de fato é o servidor** — a validação daqui
 * é conveniência, nunca a fronteira de segurança.
 */
import { useCallback, useMemo, useState } from "react";
import type { Perfil, UsuarioResumo } from "@co/contracts";
import { api } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  Selo,
  Tabela,
  classeEntrada,
  type Tom,
} from "../ui/base.js";
import {
  Conteudo,
  Gaveta,
  Grade,
  Indicadores,
  LinhaVazia,
  Marcar,
  Td,
  fmtDataHora,
  useCarga,
} from "../ui/comercial.js";

/* ══════════════════════════════════════════════════════════════════════
   O modelo de perfis. `nivel` menor é mais alto na hierarquia.
   ══════════════════════════════════════════════════════════════════════ */

interface DefPerfil {
  rotulo: string;
  nivel: number;
  tom: Tom;
  escopo: string;
}

const PERFIS: Record<Perfil, DefPerfil> = {
  administrador: {
    rotulo: "Administrador",
    nivel: 0,
    tom: "crit",
    escopo: "Todos os registros da plataforma",
  },
  gerente: {
    rotulo: "Gerente",
    nivel: 1,
    tom: "ember",
    escopo: "Sua operação e as equipes abaixo",
  },
  supervisor: {
    rotulo: "Supervisor",
    nivel: 2,
    tom: "cool",
    escopo: "Os usuários sob sua responsabilidade",
  },
  consultor: {
    rotulo: "Consultor/Vendedor",
    nivel: 3,
    tom: "ok",
    escopo: "Apenas a própria carteira",
  },
};

const ORDEM: readonly Perfil[] = [
  "administrador",
  "gerente",
  "supervisor",
  "consultor",
];

/** As cinco ações do roadmap §2.2. A tela mostra o modelo; o servidor aplica. */
const ACOES = [
  "visualizar",
  "criar",
  "editar",
  "excluir",
  "administrar",
] as const;

const PERMISSOES_POR_PERFIL: Record<Perfil, ReadonlyArray<(typeof ACOES)[number]>> =
  {
    administrador: ["visualizar", "criar", "editar", "excluir", "administrar"],
    gerente: ["visualizar", "criar", "editar", "excluir"],
    supervisor: ["visualizar", "criar", "editar"],
    consultor: ["visualizar", "criar"],
  };

const nivelDe = (u: UsuarioResumo): number => PERFIS[u.perfil].nivel;

/* ══════════════════════════════════════════════════════════════════════
   Cadeia — descendentes, ciclo e superiores possíveis
   ══════════════════════════════════════════════════════════════════════ */

const subordinados = (us: UsuarioResumo[], id: string): UsuarioResumo[] =>
  us.filter((u) => u.supervisorId === id);

/** Toda a subárvore abaixo do usuário, sem ele. Para no primeiro ciclo. */
function descendentes(us: UsuarioResumo[], id: string): UsuarioResumo[] {
  const saida: UsuarioResumo[] = [];
  const vistos = new Set<string>([id]);
  const fila = subordinados(us, id).slice();
  while (fila.length > 0) {
    const u = fila.shift();
    if (!u || vistos.has(u.id)) continue;
    vistos.add(u.id);
    saida.push(u);
    for (const f of subordinados(us, u.id)) fila.push(f);
  }
  return saida;
}

/**
 * Superiores válidos: ativo, mais alto na hierarquia que o perfil escolhido
 * e fora da subárvore do próprio usuário — essa última condição é o que
 * impede fechar ciclo.
 */
function superioresPossiveis(
  us: UsuarioResumo[],
  perfil: Perfil,
  idEmEdicao: string | null,
): UsuarioResumo[] {
  const nivel = PERFIS[perfil].nivel;
  const abaixo = idEmEdicao
    ? new Set(descendentes(us, idEmEdicao).map((u) => u.id))
    : new Set<string>();
  return us
    .filter(
      (u) =>
        u.id !== idEmEdicao &&
        u.ativo &&
        nivelDe(u) < nivel &&
        !abaixo.has(u.id),
    )
    .sort((a, b) => nivelDe(a) - nivelDe(b) || a.nome.localeCompare(b.nome, "pt-BR"));
}

interface Rascunho {
  nome: string;
  email: string;
  perfil: Perfil;
  supervisorId: string;
  ativo: boolean;
}

/** Roda antes de gravar. Devolve a frase do problema, ou `null` se passou. */
function validar(
  us: UsuarioResumo[],
  dados: Rascunho,
  idEmEdicao: string | null,
): string | null {
  const nome = dados.nome.trim();
  const email = dados.email.trim();

  if (nome.length < 2) return "Informe o nome.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
    return "Informe um e-mail válido.";
  if (
    us.some(
      (u) => u.id !== idEmEdicao && u.email.toLowerCase() === email.toLowerCase(),
    )
  )
    return "Já existe usuário com esse e-mail.";

  const ehTopo = dados.perfil === "administrador";
  if (ehTopo && dados.supervisorId !== "")
    return "Administrador não responde a ninguém.";
  if (!ehTopo && dados.supervisorId === "")
    return "Escolha a quem este usuário responde.";

  if (dados.supervisorId !== "") {
    const sup = us.find((u) => u.id === dados.supervisorId);
    if (!sup) return "O superior escolhido não existe.";
    if (!sup.ativo) return "O superior escolhido está inativo.";
    if (nivelDe(sup) >= PERFIS[dados.perfil].nivel)
      return `O superior precisa estar acima de ${PERFIS[dados.perfil].rotulo} na hierarquia.`;
    if (
      idEmEdicao &&
      (sup.id === idEmEdicao ||
        descendentes(us, idEmEdicao).some((u) => u.id === sup.id))
    )
      return "Isso criaria um ciclo: o superior escolhido está abaixo deste usuário.";
  }

  // Rebaixar alguém não pode deixar subordinado acima do próprio chefe.
  if (idEmEdicao) {
    const novoNivel = PERFIS[dados.perfil].nivel;
    const conflito = subordinados(us, idEmEdicao).find(
      (f) => nivelDe(f) <= novoNivel,
    );
    if (conflito)
      return `“${conflito.nome}” responde a este usuário e é ${PERFIS[conflito.perfil].rotulo}. Mova essa pessoa antes de trocar o perfil.`;
  }

  // Nunca ficar sem administrador ativo.
  const outrosAdmins = us.filter(
    (u) => u.ativo && u.perfil === "administrador" && u.id !== idEmEdicao,
  ).length;
  const viraAdminAtivo = dados.perfil === "administrador" && dados.ativo;
  if (outrosAdmins === 0 && !viraAdminAtivo)
    return "Precisa sobrar pelo menos um administrador ativo.";

  return null;
}

/* ══════════════════════════════════════════════════════════════════════
   Tela
   ══════════════════════════════════════════════════════════════════════ */

export function Usuarios(): JSX.Element {
  const buscar = useCallback(async (): Promise<UsuarioResumo[]> => {
    const r = await api.usuarios();
    return r.itens;
  }, []);
  const { estado, recarregar } = useCarga(buscar);

  return (
    <Conteudo estado={estado} aoRecarregar={recarregar}>
      {(us) => <CorpoDeUsuarios us={us} aoRecarregar={recarregar} />}
    </Conteudo>
  );
}

function CorpoDeUsuarios({
  us,
  aoRecarregar,
}: {
  us: UsuarioResumo[];
  aoRecarregar: () => void;
}): JSX.Element {
  const [editando, setEditando] = useState<UsuarioResumo | "novo" | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [senhaDe, setSenhaDe] = useState<{
    nome: string;
    email: string;
    senha: string;
  } | null>(null);

  // Redefinir é a única saída quando alguém perde a senha: o banco guarda
  // hash, não a senha. Confirmação explícita porque invalida o acesso atual.
  async function redefinir(u: UsuarioResumo): Promise<void> {
    if (
      !window.confirm(
        `Gerar nova senha para ${u.nome}?

A senha atual dele deixa de funcionar na hora.`,
      )
    ) {
      return;
    }
    setOcupado(u.id);
    setErro(null);
    try {
      const r = await api.redefinirSenha(u.id);
      setSenhaDe({ nome: u.nome, email: u.email, senha: r.senhaTemporaria });
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "Não consegui redefinir a senha.");
    } finally {
      setOcupado(null);
    }
  }

  const ativos = us.filter((u) => u.ativo).length;
  const adminsAtivos = us.filter(
    (u) => u.ativo && u.perfil === "administrador",
  ).length;

  /**
   * Árvore. `vistos` impede laço infinito se a cadeia chegar corrompida do
   * servidor — a validação barra ciclo na gravação, mas a tela não pode
   * travar o navegador por causa de um dado ruim.
   */
  const { linhas, orfaos } = useMemo(() => {
    const vistos = new Set<string>();
    const saida: Array<{ u: UsuarioResumo; prof: number }> = [];
    const ordenar = (a: UsuarioResumo, b: UsuarioResumo): number =>
      nivelDe(a) - nivelDe(b) || a.nome.localeCompare(b.nome, "pt-BR");

    const ramo = (u: UsuarioResumo, prof: number): void => {
      if (vistos.has(u.id)) return;
      vistos.add(u.id);
      saida.push({ u, prof });
      for (const s of subordinados(us, u.id).slice().sort(ordenar))
        ramo(s, prof + 1);
    };

    const raizes = us
      .filter((u) => !u.supervisorId || !us.some((o) => o.id === u.supervisorId))
      .slice()
      .sort(ordenar);
    for (const r of raizes) ramo(r, 0);

    return { linhas: saida, orfaos: us.filter((u) => !vistos.has(u.id)) };
  }, [us]);

  const semDono = us.filter(
    (u) => u.perfil === "consultor" && u.numerosNoEscopo === 0,
  ).length;

  async function alternar(u: UsuarioResumo): Promise<void> {
    setOcupado(u.id);
    setErro(null);
    try {
      await api.editarUsuario(u.id, { ativo: !u.ativo });
      aoRecarregar();
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "Não foi possível alterar.");
    } finally {
      setOcupado(null);
    }
  }

  async function excluir(u: UsuarioResumo): Promise<void> {
    setOcupado(u.id);
    setErro(null);
    try {
      await api.excluirUsuario(u.id);
      aoRecarregar();
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "Não foi possível excluir.");
    } finally {
      setOcupado(null);
    }
  }

  /** Por que um botão está travado, em texto — nunca só desabilitado. */
  function travaDe(u: UsuarioResumo): string | null {
    if (u.ativo && u.perfil === "administrador" && adminsAtivos === 1)
      return "É o único administrador ativo — a plataforma ficaria sem quem administra.";
    const diretos = subordinados(us, u.id).length;
    if (diretos > 0)
      return `${diretos} usuário(s) respondem a este. Mova essas pessoas antes.`;
    return null;
  }

  return (
    <div className="grid gap-4">
      <Indicadores>
        <Indicador
          rotulo="Usuários"
          valor={us.length}
          nota={`${ativos} ativo(s)`}
        />
        {ORDEM.map((p) => (
          <Indicador
            key={p}
            rotulo={PERFIS[p].rotulo}
            valor={us.filter((u) => u.perfil === p && u.ativo).length}
            nota={PERFIS[p].escopo}
          />
        ))}
      </Indicadores>

      {erro && <Aviso tom="crit">{erro}</Aviso>}

      {adminsAtivos === 0 && (
        <Aviso tom="crit">
          <strong>Nenhum administrador ativo.</strong> Sem ele ninguém tem
          escopo sobre a plataforma inteira, e nenhum registro sem dono aparece
          para alguém. Reative ou crie um administrador antes de qualquer outra
          coisa.
        </Aviso>
      )}

      {/* ── Hierarquia ───────────────────────────────────────────── */}
      <Painel
        titulo="Hierarquia"
        dica="Quem responde a quem — e, por consequência, quem enxerga o quê"
        acoes={
          <Botao tipo="principal" pequeno onClick={() => setEditando("novo")}>
            Novo usuário
          </Botao>
        }
        semCorpo
      >
        <div className="p-2 overflow-x-auto">
          {linhas.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-tinta-3">
              Nenhum usuário cadastrado.
            </p>
          ) : (
            <ul className="min-w-[560px]">
              {linhas.map(({ u, prof }) => (
                <li key={u.id}>
                  <div
                    className={`flex items-center gap-2.5 flex-wrap rounded-lg px-3 py-2 hover:bg-superficie-2 ${
                      u.ativo ? "" : "opacity-60"
                    }`}
                    style={{ paddingLeft: prof * 22 + 12 }}
                  >
                    {prof > 0 && (
                      <span aria-hidden="true" className="text-tinta-3 text-xs">
                        └
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditando(u)}
                      className="text-[13px] font-medium text-tinta hover:text-marca rounded-sm"
                    >
                      {u.nome}
                    </button>
                    <Selo tom={PERFIS[u.perfil].tom}>
                      {PERFIS[u.perfil].rotulo}
                    </Selo>
                    {!u.ativo && <Selo tom="idle">inativo</Selo>}
                    <span className="font-mono text-[11.5px] text-tinta-3">
                      {u.email}
                    </span>
                    <span className="ml-auto text-[11.5px] text-tinta-3 tabular whitespace-nowrap">
                      {u.numerosNoEscopo} número(s)
                      {subordinados(us, u.id).length > 0
                        ? ` · ${subordinados(us, u.id).length} direto(s)`
                        : ""}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {orfaos.length > 0 && (
            <div className="m-3">
              <Aviso tom="crit">
                <strong>
                  {orfaos.length} usuário(s) fora da árvore
                </strong>{" "}
                — a cadeia de superiores está quebrada ou fechou ciclo:{" "}
                {orfaos.map((o) => o.nome).join(", ")}. Enquanto isso durar, os
                registros dessas pessoas não entram no escopo de ninguém acima
                delas. Edite e escolha um superior válido.
              </Aviso>
            </div>
          )}
        </div>
      </Painel>

      {semDono > 0 && (
        <Aviso tom="warn">
          <strong>{semDono} consultor(es) sem número no escopo.</strong> Quem
          não tem número atribuído não recebe carteira e não aparece em nenhum
          recorte por responsável. Atribua na ficha do número.
        </Aviso>
      )}

      {/* ── Tabela ───────────────────────────────────────────────── */}
      <Painel
        titulo="Todos os usuários"
        dica={`${us.length} cadastrado(s)`}
        semCorpo
      >
        <Tabela
          larguraMin={900}
          colunas={[
            { chave: "u", rotulo: "Usuário" },
            { chave: "perfil", rotulo: "Perfil" },
            { chave: "sup", rotulo: "Responde a" },
            { chave: "nums", rotulo: "Números no escopo", direita: true },
            { chave: "sit", rotulo: "Situação" },
            { chave: "criado", rotulo: "Criado" },
            { chave: "acoes", rotulo: "", direita: true },
          ]}
        >
          {us.length === 0 ? (
            <LinhaVazia colunas={7}>Nenhum usuário cadastrado.</LinhaVazia>
          ) : (
            us
              .slice()
              .sort(
                (a, b) =>
                  nivelDe(a) - nivelDe(b) ||
                  a.nome.localeCompare(b.nome, "pt-BR"),
              )
              .map((u) => {
                const trava = travaDe(u);
                return (
                  <tr key={u.id}>
                    <Td>
                      <div className="font-medium">{u.nome}</div>
                      <div className="font-mono text-[11.5px] text-tinta-3 mt-0.5">
                        {u.email}
                      </div>
                    </Td>
                    <Td>
                      <Selo tom={PERFIS[u.perfil].tom}>
                        {PERFIS[u.perfil].rotulo}
                      </Selo>
                    </Td>
                    <Td className="text-[12.5px]">
                      {u.supervisorNome ?? (
                        <span className="text-tinta-3">
                          {u.perfil === "administrador" ? "ninguém" : "—"}
                        </span>
                      )}
                    </Td>
                    <Td direita>{u.numerosNoEscopo}</Td>
                    <Td>
                      <Selo tom={u.ativo ? "ok" : "idle"}>
                        {u.ativo ? "ativo" : "inativo"}
                      </Selo>
                    </Td>
                    <Td className="text-[11.5px] text-tinta-3 whitespace-nowrap">
                      {fmtDataHora(u.criadoEm)}
                    </Td>
                    <Td direita>
                      <div className="flex gap-1.5 justify-end flex-wrap">
                        <Botao
                          pequeno
                          tipo="fantasma"
                          onClick={() => setEditando(u)}
                        >
                          Editar
                        </Botao>
                        <Botao
                          pequeno
                          tipo="fantasma"
                          desabilitado={ocupado === u.id}
                          titulo="Gera uma senha nova e invalida a atual"
                          onClick={() => void redefinir(u)}
                        >
                          Senha
                        </Botao>
                        <Botao
                          pequeno
                          tipo="fantasma"
                          desabilitado={
                            ocupado === u.id ||
                            (u.ativo &&
                              u.perfil === "administrador" &&
                              adminsAtivos === 1)
                          }
                          {...(trava ? { titulo: trava } : {})}
                          onClick={() => void alternar(u)}
                        >
                          {u.ativo ? "Desativar" : "Reativar"}
                        </Botao>
                        <Botao
                          pequeno
                          tipo="perigo"
                          desabilitado={ocupado === u.id || trava !== null}
                          {...(trava ? { titulo: trava } : {})}
                          onClick={() => void excluir(u)}
                        >
                          Excluir
                        </Botao>
                      </div>
                      {trava && (
                        <p className="text-[11px] text-tinta-3 mt-1 max-w-[34ch] ml-auto leading-relaxed">
                          {trava}
                        </p>
                      )}
                    </Td>
                  </tr>
                );
              })
          )}
        </Tabela>
      </Painel>

      {/* ── Permissão × escopo ───────────────────────────────────── */}
      <Painel
        titulo="Permissões por perfil"
        dica="O que cada perfil pode fazer — e sobre quais registros"
        semCorpo
      >
        <Tabela
          larguraMin={860}
          colunas={[
            { chave: "perfil", rotulo: "Perfil" },
            ...ACOES.map((a) => ({ chave: a, rotulo: a })),
            { chave: "escopo", rotulo: "Escopo — sobre quais registros" },
          ]}
        >
          {ORDEM.map((p) => (
            <tr key={p}>
              <Td>
                <Selo tom={PERFIS[p].tom}>{PERFIS[p].rotulo}</Selo>
              </Td>
              {ACOES.map((a) => (
                <Td key={a}>
                  {PERMISSOES_POR_PERFIL[p].includes(a) ? (
                    <span className="text-ok" aria-label="permitido">
                      sim
                    </span>
                  ) : (
                    <span className="text-tinta-3" aria-label="não permitido">
                      não
                    </span>
                  )}
                </Td>
              ))}
              <Td className="text-[12.5px] text-tinta-2">{PERFIS[p].escopo}</Td>
            </tr>
          ))}
        </Tabela>
        <div className="p-5 border-t border-linha">
          <Aviso tom="cool">
            <strong>Permissão e escopo são checagens diferentes.</strong> A
            permissão responde <em>"pode editar?"</em>; o escopo responde{" "}
            <em>"editar qual registro?"</em>. Um supervisor com permissão de
            editar só edita dentro da própria equipe. Esta tabela mostra o
            modelo; <strong>quem aplica é o servidor</strong> — nada aqui
            bloqueia acesso por si só.
          </Aviso>
        </div>
      </Painel>

      {senhaDe && (
        <PainelSenha
          nome={senhaDe.nome}
          email={senhaDe.email}
          senha={senhaDe.senha}
          aoFechar={() => setSenhaDe(null)}
        />
      )}

      {editando && (
        <FormularioUsuario
          us={us}
          alvo={editando === "novo" ? null : editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => {
            setEditando(null);
            aoRecarregar();
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Formulário
   ══════════════════════════════════════════════════════════════════════ */

function FormularioUsuario({
  us,
  alvo,
  aoFechar,
  aoSalvar,
}: {
  us: UsuarioResumo[];
  alvo: UsuarioResumo | null;
  aoFechar: () => void;
  aoSalvar: () => void;
}): JSX.Element {
  const [dados, setDados] = useState<Rascunho>({
    nome: alvo?.nome ?? "",
    email: alvo?.email ?? "",
    perfil: alvo?.perfil ?? "consultor",
    supervisorId: alvo?.supervisorId ?? "",
    ativo: alvo?.ativo ?? true,
  });
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // A senha só existe em texto neste instante. Se a gaveta fechar sem
  // ninguém copiar, a saída é redefinir — o banco guarda apenas o hash.
  const [senhaNova, setSenhaNova] = useState<string | null>(null);

  const ehTopo = dados.perfil === "administrador";
  const superiores = useMemo(
    () => superioresPossiveis(us, dados.perfil, alvo?.id ?? null),
    [us, dados.perfil, alvo],
  );

  // O veredito aparece enquanto se digita, não só depois de clicar salvar.
  const problema = validar(us, dados, alvo?.id ?? null);

  async function salvar(): Promise<void> {
    if (problema) {
      setErro(problema);
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      const corpo = {
        nome: dados.nome.trim(),
        email: dados.email.trim(),
        perfil: dados.perfil,
        supervisorId: ehTopo ? null : dados.supervisorId,
        ativo: dados.ativo,
      };
      if (alvo) {
        await api.editarUsuario(alvo.id, corpo);
        aoSalvar();
        return;
      }
      const criado = await api.criarUsuario(corpo);
      // Não fecha: fechar aqui perderia a única exibição da senha.
      setSenhaNova(criado.senhaTemporaria);
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "O servidor recusou o cadastro.");
    } finally {
      setOcupado(false);
    }
  }

  if (senhaNova) {
    return (
      <PainelSenha
        nome={dados.nome.trim()}
        email={dados.email.trim()}
        senha={senhaNova}
        aoFechar={aoSalvar}
      />
    );
  }

  return (
    <Gaveta
      titulo={alvo ? `Editar ${alvo.nome}` : "Novo usuário"}
      dica="Perfil define o que pode fazer; o superior define sobre o que"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao
            tipo="principal"
            desabilitado={ocupado || problema !== null}
            onClick={() => void salvar()}
          >
            {ocupado ? "Salvando…" : "Salvar"}
          </Botao>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          {problema && (
            <span className="text-[12px] text-tinta-3 ml-auto max-w-[40ch] leading-relaxed">
              {problema}
            </span>
          )}
        </>
      }
    >
      <div className="grid gap-4">
        {erro && <Aviso tom="crit">{erro}</Aviso>}

        <Grade colunas={2}>
          <Campo rotulo="Nome">
            <input
              className={classeEntrada}
              value={dados.nome}
              onChange={(e) => setDados({ ...dados, nome: e.target.value })}
            />
          </Campo>
          <Campo rotulo="E-mail" dica="Identifica a pessoa no histórico">
            <input
              type="email"
              className={classeEntrada}
              value={dados.email}
              onChange={(e) => setDados({ ...dados, email: e.target.value })}
            />
          </Campo>
          <Campo rotulo="Perfil" dica={PERFIS[dados.perfil].escopo}>
            <select
              className={classeEntrada}
              value={dados.perfil}
              onChange={(e) => {
                const p = e.target.value as Perfil;
                // Trocar de perfil pode invalidar o superior: quem virou
                // administrador não responde a ninguém, e quem subiu de nível
                // pode ter ficado com um superior que não está mais acima.
                setDados((d) => ({
                  ...d,
                  perfil: p,
                  supervisorId:
                    p === "administrador"
                      ? ""
                      : superioresPossiveis(us, p, alvo?.id ?? null).some(
                            (s) => s.id === d.supervisorId,
                          )
                        ? d.supervisorId
                        : "",
                }));
              }}
            >
              {ORDEM.map((p) => (
                <option key={p} value={p}>
                  {PERFIS[p].rotulo}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Responde a"
            dica={
              ehTopo
                ? "Administrador não responde a ninguém"
                : "Só aparecem usuários ativos acima deste perfil e fora da sua própria equipe"
            }
          >
            <select
              className={classeEntrada}
              value={dados.supervisorId}
              disabled={ehTopo}
              onChange={(e) =>
                setDados({ ...dados, supervisorId: e.target.value })
              }
            >
              <option value="">
                {ehTopo ? "— ninguém —" : "Escolha o superior…"}
              </option>
              {superiores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome} — {PERFIS[s.perfil].rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </Grade>

        {!ehTopo && superiores.length === 0 && (
          <Aviso tom="warn">
            Não há superior possível para este perfil. Ou não existe ninguém
            ativo acima dele, ou todos os candidatos estão abaixo deste usuário
            na cadeia — nesse caso escolher qualquer um fecharia ciclo.
          </Aviso>
        )}

        <Marcar
          marcado={dados.ativo}
          aoMudar={(v) => setDados({ ...dados, ativo: v })}
          rotulo="Usuário ativo"
          dica="Inativo não entra em distribuição de lead nem pode ser superior de ninguém. O histórico dele permanece."
        />

        <Aviso tom="cool">
          <strong>Quatro regras que este formulário faz cumprir:</strong>{" "}
          administrador não responde a ninguém; todos os outros perfis exigem
          superior; a plataforma nunca fica sem administrador ativo; e nenhuma
          escolha pode fechar ciclo na cadeia. O servidor confere de novo.
        </Aviso>
      </div>
    </Gaveta>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Entrega da senha

   Aparece uma vez e some. O servidor guarda apenas o hash: se esta gaveta
   fechar sem ninguém copiar, não existe "ver de novo" — existe redefinir.
   Por isso ela não tem botão de cancelar, e o rótulo diz o que é.
   ══════════════════════════════════════════════════════════════════════ */
function PainelSenha({
  nome,
  email,
  senha,
  aoFechar,
}: {
  nome: string;
  email: string;
  senha: string;
  aoFechar: () => void;
}): JSX.Element {
  const [copiado, setCopiado] = useState(false);

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(senha);
      setCopiado(true);
    } catch {
      // Sem permissão de área de transferência: o campo continua selecionável.
      setCopiado(false);
    }
  }

  return (
    <Gaveta
      titulo={`Senha de ${nome || "acesso"}`}
      dica="Aparece uma única vez — copie antes de fechar"
      aoFechar={aoFechar}
      rodape={
        <Botao tipo="principal" onClick={aoFechar}>
          Já anotei, pode fechar
        </Botao>
      }
    >
      <div className="grid gap-4">
        <Aviso tom="ember">
          O sistema guarda só o <strong>hash</strong> desta senha. Depois que
          esta gaveta fechar, ninguém — nem o administrador — consegue lê-la de
          novo. O caminho passa a ser o botão <strong>Senha</strong> na lista,
          que gera outra e invalida esta.
        </Aviso>

        <Campo rotulo="Entra com este e-mail">
          <input className={classeEntrada} readOnly value={email} />
        </Campo>

        <Campo rotulo="E esta senha">
          <div className="flex gap-2">
            <input
              className={`${classeEntrada} font-mono tracking-wide`}
              readOnly
              value={senha}
              onFocus={(e) => e.currentTarget.select()}
            />
            <Botao tipo="normal" onClick={() => void copiar()}>
              {copiado ? "Copiado" : "Copiar"}
            </Botao>
          </div>
        </Campo>

        <p className="text-[12px] text-tinta-3 leading-relaxed">
          Mande por um canal que você confia — e peça para a pessoa trocar no
          primeiro acesso. Senha temporária circulando em grupo é credencial
          compartilhada, e credencial compartilhada apaga a autoria da
          auditoria.
        </p>
      </div>
    </Gaveta>
  );
}
